import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  TableContainer,
} from '@mui/material';
import { WithStyles } from '@mui/styles';
import withStyles from '@mui/styles/withStyles';
import cx from 'classnames';
import { IMedispanStagingDiff } from 'models/application-manager/medispan/IMedispanStagingDiff';
import { IMedispanDrugRecord } from 'models/application-manager/medispan/IMedispanDrugRecord';
import { medispanStagingStyles } from './index.styles';

type SortableField = 'drugName' | 'strength' | 'dosageForm' | 'routeOfAdministration' | 'gpi';
type SortDirection = 'asc' | 'desc';

interface IMedispanStagingTableProps extends WithStyles<typeof medispanStagingStyles> {
  rows: IMedispanStagingDiff[];
}

const MedispanStagingTable = (props: IMedispanStagingTableProps): JSX.Element => {
  const { classes, rows } = props;

  const [sortField, setSortField] = React.useState<SortableField>('gpi');
  const [sortDir, setSortDir] = React.useState<SortDirection>('asc');

  const handleSort = (field: SortableField) => {
    if (field === sortField) {
      setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortedRows = React.useMemo(() => {
    return [...rows].sort((a, b) => {
      const aVal = (a.current ?? a.staged)?.[sortField]?.toString() ?? '';
      const bVal = (b.current ?? b.staged)?.[sortField]?.toString() ?? '';
      const cmp = aVal.localeCompare(bVal);
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [rows, sortField, sortDir]);

  const sortLabel = (field: SortableField, label: string) => (
    <TableSortLabel
      active={sortField === field}
      direction={sortDir}
      onClick={() => handleSort(field)}
    >
      {label}
    </TableSortLabel>
  );

  return (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Drug Descriptor ID</TableCell>
            <TableCell colSpan={2}>{sortLabel('gpi', 'GPI')}</TableCell>
            <TableCell colSpan={2}>{sortLabel('drugName', 'Drug Name')}</TableCell>
            <TableCell colSpan={2}>{sortLabel('strength', 'Strength')}</TableCell>
            <TableCell colSpan={2}>{sortLabel('dosageForm', 'Dosage Form')}</TableCell>
            <TableCell colSpan={2}>{sortLabel('routeOfAdministration', 'Route')}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell />
            {['GPI', 'Drug Name', 'Strength', 'Dosage Form', 'Route'].map(col => (
              <React.Fragment key={col}>
                <TableCell className={classes.currentCell}>Current</TableCell>
                <TableCell>Staged</TableCell>
              </React.Fragment>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedRows.map((row, idx) => {
            const renderPair = (field: keyof IMedispanDrugRecord) => {
              const currentVal = row.current?.[field]?.toString() ?? '—';
              const stagedVal = row.staged?.[field]?.toString() ?? '—';
              const isDifferent = currentVal !== stagedVal;
              return (
                <React.Fragment key={field}>
                  <TableCell className={classes.currentCell}>{currentVal}</TableCell>
                  <TableCell
                    className={cx(classes.stagedCell, { [classes.changedValue]: isDifferent })}
                  >
                    {stagedVal}
                  </TableCell>
                </React.Fragment>
              );
            };

            return (
              <TableRow key={`${row.drugDescriptorId}-${idx}`} className={classes.rowModified}>
                <TableCell>{row.drugDescriptorId}</TableCell>
                {renderPair('gpi')}
                {renderPair('drugName')}
                {renderPair('strength')}
                {renderPair('dosageForm')}
                {renderPair('routeOfAdministration')}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export const MedispanStagingTableStyled = withStyles(medispanStagingStyles)(MedispanStagingTable);
